import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import {
	type PostPersonioAutoIntegrationSchema,
	postPersonioAutoIntegrationSchema,
} from "@msuite/utils/schemas";
import { useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { IntegrationPreCheck } from "@/components/integrations";
import { PersonioSync } from "@/components/personio-sync";
import type { PersonioSyncRun } from "@/components/personio-sync/utils";
import { client } from "@/hono";
import { PERSONIO_SYNC_QUERY_KEY } from "@/hooks/use-personio-sync";

/** Props Interface */
interface PersonioSyncModalProps extends ModalProps {
	onComplete: () => Promise<unknown>;
}

export function PersonioSyncModal({ isOpen, onClose, onComplete }: PersonioSyncModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const methods = useForm<PostPersonioAutoIntegrationSchema>({
		resolver: resolver(postPersonioAutoIntegrationSchema),
		defaultValues: { items: [] },
	});
	const items = useWatch({ control: methods.control, name: "items" });

	/** State */
	const [run, setRun] = useState<PersonioSyncRun | null>(null);
	const [runCount, setRunCount] = useState(0);

	/** Functions */
	async function handleOnSubmit(json: PostPersonioAutoIntegrationSchema) {
		try {
			const response = await client.integrations.personio["auto-integration"].$post({ json });
			const data = await responseHandler(response);
			setRun({ results: data.results, areTeamsSynced: data.areTeamsSynced });
			// A fresh table: the rows that went through are linked now, and the selection and the
			// choices made by hand belonged to the run that just finished.
			setRunCount((count) => count + 1);
			methods.reset({ items: [] });
			await qc.invalidateQueries({ predicate: invalidate(PERSONIO_SYNC_QUERY_KEY) });
			await onComplete();
		} catch (error) {
			// `responseHandler` has already put the refusal in a toast — a second sync still
			// running answers 409 with its own message — and the selection stays for a retry.
			console.error(error);
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("integrations.personio_sync.title")}
				width="95vw"
				isLoading={methods.formState.isLoading}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					<IntegrationPreCheck integrationType="personio">
						<PersonioSync
							key={runCount}
							run={run}
							onSelectionChange={(next) => methods.setValue("items", next)}
						/>
					</IntegrationPreCheck>
				</Modal.Body>
				<Modal.FooterForm
					cancelLabel={t("general.close")}
					submitLabel={t("integrations.personio_sync.sync_n_people", {
						count: items.length.toString(),
					})}
					isDisabled={!items.length}
				/>
			</Modal>
		</FormProvider>
	);
}
